import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { useCreateShareableEntityNote } from "@/lib/api/shareable-entities";
import { Button } from "@somethings-ui/web";
import { useState } from "react";
import { useHubStore } from "../use-hub-store";
import { EntityHeader } from "../entity-header";
import { NoteView } from "./note-view";
import { usePostHogEvent } from "@/lib/posthog";
import { useCurrentUser } from "@/lib/api/user";
import { ScheduleFooterButton } from "../scheduling/schedule-footer-button";
import { hubToast } from "../hub-toasts";

interface PreviewNoteViewProps {
  mentorship: MentorshipDetails;
}

export function PreviewNoteView({ mentorship }: PreviewNoteViewProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const draftNoteContent = useHubStore((state) => state.draftNoteContent);
  const navigateToView = useHubStore((state) => state.navigateToView);
  const setDraftNoteContent = useHubStore((state) => state.setDraftNoteContent);
  const createNote = useCreateShareableEntityNote(mentorship.mentee.id);
  const { dispatchEvent } = usePostHogEvent("mentor_hub", { onMount: false });

  const { data: user } = useCurrentUser();

  const handleSchedule = async (scheduledAt: Date) => {
    if (draftNoteContent?.trim()) {
      try {
        await createNote.mutateAsync({
          content: draftNoteContent.trim(),
          title: null,
          noteType: null,
          shareWithId: mentorship.mentee.id,
          schedule: { scheduleAt: scheduledAt.toISOString() },
        });
        dispatchEvent("mentor_note_scheduled");
        hubToast.scheduled("note");
        setDraftNoteContent(null);
        navigateToView("list");
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleSend = async () => {
    if (draftNoteContent?.trim()) {
      try {
        await createNote.mutateAsync({
          content: draftNoteContent.trim(),
          title: null,
          noteType: null,
          shareWithId: mentorship.mentee.id,
        });
        dispatchEvent("mentor_hub_note_shared");
        setDraftNoteContent(null);
        navigateToView("list");
      } catch (error) {
        console.error(error);
      }
      setIsDialogOpen(false);
    }
  };

  return (
    <>
      <div className="flex h-full flex-col bg-white">
        <EntityHeader title="Preview" showInfo={true} />
        <div className="flex-1 overflow-y-auto p-6">
          <NoteView
            content={draftNoteContent || ""}
            editMode={false}
            placeholder="Start writing your note..."
            maxLength={620}
            creatorAvatarUrl={user?.profile_picture_url ?? undefined}
            creatorName={user?.name ?? undefined}
          />
        </div>
        <div className="space-y-3 border-t border-zinc-300 bg-white p-4">
          <ScheduleFooterButton
            entityType="note"
            disabled={!draftNoteContent?.trim() || createNote.isPending}
            loading={createNote.isPending}
            onSchedule={handleSchedule}
          />
          <Button
            onClick={() => setIsDialogOpen(true)}
            disabled={!draftNoteContent?.trim() || createNote.isPending}
            variant="primary"
            shape="pill"
            className="w-full"
          >
            Send
          </Button>
        </div>
      </div>
      <ConfirmActionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleSend}
        title="Are you sure you want to send Note?"
        description="You won't be able to edit or delete the note afterward."
        loading={createNote.isPending}
        confirmVariant="default"
        confirmText="Send"
      />
    </>
  );
}
